import { FC, Fragment, useCallback, useEffect, useRef, useState } from 'react';

import handleEnterKeyPress from 'common/handleEnterKeypress';

import locales from '../../locales';
import {
  Container,
  ContentWrapper,
  OnDragContent,
  OnDragContentText,
} from './styles';
import { DropZoneProps } from './types';

const setDraggingTrueFactory =
  (mutator: (value: boolean) => void): ((event: Event) => void) =>
  (event: Event): void => {
    event.preventDefault();
    mutator(true);
  };
const setDraggingFalseFactory =
  (mutator: (value: boolean) => void): ((event: Event) => void) =>
  (event: Event): void => {
    event.preventDefault();
    mutator(false);
  };
const preventMisdropFactory =
  (mutator: (value: boolean) => void): ((event: Event) => void) =>
  (event: Event): void => {
    event.preventDefault();
    mutator(false);
  };

const DropZone: FC<DropZoneProps> = ({
  accept,
  backgroundColor = 'grey.200',
  children,
  compactStyle,
  dashedBorderColor = 'grey.400',
  disabled = false,
  documentName = locales.document,
  onUpload,
  withFlatBottom,
}) => {
  const [dragging, setDragging] = useState(false);
  const [isOverDropZone, setIsOverDropZone] = useState(false);
  const inputRef = useRef<HTMLInputElement | null>(null);

  useEffect((): (() => void) => {
    const { body } = document;

    const setDraggingTrue = setDraggingTrueFactory(setDragging);
    const setDraggingFalse = setDraggingFalseFactory(setDragging);
    const preventMisdrop = preventMisdropFactory(setDragging);

    if (!disabled) {
      body.addEventListener('dragover', setDraggingTrue);
      body.addEventListener('dragleave', setDraggingFalse);
      body.addEventListener('drop', preventMisdrop);
    }

    return (): void => {
      body.removeEventListener('dragover', setDraggingTrue);
      body.removeEventListener('dragleave', setDraggingFalse);
      body.removeEventListener('drop', preventMisdrop);
    };
  }, [disabled]);

  const onClick = useCallback((): void => {
    if (!disabled && inputRef.current !== null) {
      inputRef.current.click();
    }
  }, [disabled, inputRef]);

  return (
    <Fragment>
      <input
        accept={accept}
        data-testid="dropzone-input"
        disabled={disabled}
        onChange={(event: React.ChangeEvent<HTMLInputElement>): void => {
          const { files } = event.currentTarget;

          if (files?.[0]) {
            onUpload(files[0], 'click');
          }
        }}
        // we empty the input value so that the onChange event can be triggered
        // again when trying to upload the same file after an unsuccessful attempt
        onClick={(event: React.MouseEvent<HTMLInputElement>): void => {
          // eslint-disable-next-line no-param-reassign
          event.currentTarget.value = '';
        }}
        ref={inputRef}
        style={{ display: 'none' }}
        type="file"
      />
      <Container
        backgroundColor={backgroundColor}
        compactStyle={compactStyle}
        onClick={onClick}
        onDragEnter={() => {
          setIsOverDropZone(true);
        }}
        onDragLeave={() => {
          setIsOverDropZone(false);
        }}
        onDrop={(event: React.DragEvent<HTMLDivElement>): void => {
          event.preventDefault();

          if (!disabled && event.dataTransfer.files[0]) {
            onUpload(event.dataTransfer.files[0], 'dragAndDrop');
          }
        }}
        onKeyPress={handleEnterKeyPress(onClick)}
        tabIndex={0}
        withFlatBottom={withFlatBottom}
      >
        <ContentWrapper
          border={compactStyle ? 'none' : '1px dashed'}
          borderColor={dashedBorderColor}
          compactStyle={compactStyle}
          withFlatBottom={withFlatBottom}
        >
          {children}
        </ContentWrapper>

        <OnDragContent
          compactStyle={compactStyle}
          dragging={dragging}
          isOverDropZone={isOverDropZone}
          withFlatBottom={withFlatBottom}
        >
          <OnDragContentText>
            {locales.formatString(locales.dropToUpload, { documentName })}
          </OnDragContentText>
        </OnDragContent>
      </Container>
    </Fragment>
  );
};

export default DropZone;
